import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";

interface IEmbedPageHeaderContext {
  headerIds: number[];
  register: (id: number) => void;
  unregister: (id: number) => void;
}

const EmbedPageHeaderContext = createContext<IEmbedPageHeaderContext>({
  headerIds: [],
  register: () => {},
  unregister: () => {},
});

let nextHeaderId = 1;

/**
 * Pamięta, które nagłówki podstron są zamontowane. Pasek narzędzi (język, logowanie, koszyk)
 * stoi przy pierwszym z nich, a gdy strona nie ma nagłówka, pokazuje go EmbedNavBar.
 */
export const EmbedPageHeaderProvider: React.FC = ({ children }) => {
  const [headerIds, setHeaderIds] = useState<number[]>([]);

  const register = useCallback((id: number) => setHeaderIds((prev) => [...prev, id]), []);
  const unregister = useCallback((id: number) => setHeaderIds((prev) => prev.filter((item) => item !== id)), []);

  const value = useMemo(() => ({ headerIds, register, unregister }), [headerIds, register, unregister]);

  return <EmbedPageHeaderContext.Provider value={value}>{children}</EmbedPageHeaderContext.Provider>;
};

/** Zwraca true, gdy ten nagłówek ma pokazać pasek narzędzi. */
export const useEmbedPageHeaderSlot = (isActive: boolean) => {
  const { headerIds, register, unregister } = useContext(EmbedPageHeaderContext);
  const idRef = useRef(0);
  if (!idRef.current) {
    idRef.current = nextHeaderId++;
  }

  useLayoutEffect(() => {
    if (!isActive) return;
    const id = idRef.current;
    register(id);
    return () => unregister(id);
  }, [isActive, register, unregister]);

  return isActive && headerIds[0] === idRef.current;
};

export const useHasEmbedPageHeader = () => useContext(EmbedPageHeaderContext).headerIds.length > 0;
